import CaseCreateAttachments from "@/components/cases/case-create-attachments";
import Alert from "@/components/core/alert";
import { Form } from "@/components/core/forms/form";
import { useAuth } from "@/hooks/use-auth";
import { useValidationError } from "@/hooks/use-validation-errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { useCaseFilingAttachmentMutation } from "../case-filing/api/queries/case-filing-queries";
import type { CaseRecord } from "../case-record/types/case-records";
import { type CaseFilingAttachmentFormSchema, caseFilingAttachmentFormSchema } from "../dashboard/types/case-type";

export interface CaseFilingAttachmentRef {
	handleAttachmentSubmit: () => Promise<boolean>;
}

const CaseFilingAttachments = forwardRef<
	CaseFilingAttachmentRef,
	{
		data?: CaseRecord;
	}
>(({ data }, ref) => {
	const form = useForm<CaseFilingAttachmentFormSchema>({
		resolver: zodResolver(caseFilingAttachmentFormSchema),
		mode: "onChange",
		defaultValues: {
			id: data?.id,
			files: data?.medias || [],
			annexes: data?.annexes || [],
			payment: data?.payment || [],
		},
	});

	const caseRecordAttachmentMutation = useCaseFilingAttachmentMutation();

	const [errors, _, getFirstError] = useValidationError();

	const [files, setFiles] = useState<File[]>([]);
	const [annexes, setAnnexFiles] = useState<File[]>([]);
	const [payment, setPaymentFiles] = useState<File[]>([]);

	// Initialize file states when data is available
	useEffect(() => {
		if (data) {
			// Convert media objects to File objects for complaints
			const mediaFiles = (data.medias || []).map((media: any) => {
				return new File([], media.name || media.file_name || "file", {
					type: media.mime_type || "application/octet-stream",
				});
			});
			setFiles(mediaFiles);

			// Convert annex media objects to File objects
			const annexFiles = (data.annexes?.[0]?.medias || []).map((media: any) => {
				return new File([], media.name || media.file_name || "file", {
					type: media.mime_type || "application/octet-stream",
				});
			});
			setAnnexFiles(annexFiles);

			// Convert payment media objects to File objects
			const paymentFiles = (data.payment || []).map((media: any) => {
				return new File([], media.name || media.file_name || "file", {
					type: media.mime_type || "application/octet-stream",
				});
			});
			setPaymentFiles(paymentFiles);
		}
	}, [data]);

	const handleAttachmentSubmit = async (): Promise<boolean> => {
		try {
			const formData = {
				id: data?.id,
				files: files,
				annexes: annexes,
				payment: payment,
			};

			const response = await caseRecordAttachmentMutation.mutateAsync(formData);
			return !!response;
		} catch (error) {
			console.error("Error submitting attachments:", error);
			return false;
		}
	};

	useImperativeHandle(ref, () => ({
		handleAttachmentSubmit,
	}));

	return (
		<div className="flex flex-col w-full max-w-base p-6 gap-6">
			<div className="flex flex-col w-full gap-3">
				<Form {...form}>
					<div className="flex flex-col w-full gap-6">
						<div className="flex flex-col w-full gap-3">
							<div className="flex flex-col w-full">
								<span className="text-lg font-semibold text-foreground-dark">Complaint and Annexes</span>
								<span className="text-base text-foreground/70">
									Attach Complaint, Annexes, and other Related Documents. Ensure all files are clear and legible.
								</span>
							</div>
							<div className="flex flex-col w-full">
								<CaseCreateAttachments
									title="Case Complaints"
									hasAttachments={true}
									initialFiles={data?.medias || []}
									onFilesChange={async (files) => {
										setFiles(files);
									}}
									onRemoveFile={async (file) => {
										setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
									}}
								/>
								{form.formState.errors.attachments && <p className="text-sm text-danger-600">At least 1 complaint document is required</p>}
							</div>
							<div className="flex flex-col w-full">
								<CaseCreateAttachments
									title="Annexes"
									hasAttachments={true}
									initialFiles={data?.annexes[0]?.medias || annexes || []}
									onFilesChange={async (files) => {
										setAnnexFiles(files);
									}}
									onRemoveFile={async (file) => {
										setAnnexFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
									}}
								/>
							</div>
							<div className="flex flex-col w-full">
								<CaseCreateAttachments
									title="Proof of Payment"
									hasAttachments={true}
									initialFiles={data?.payment || []}
									onFilesChange={async (files) => {
										setPaymentFiles(files);
									}}
									onRemoveFile={async (file) => {
										setPaymentFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
									}}
								/>
							</div>
						</div>
					</div>
				</Form>
			</div>
		</div>
	);
});

export default CaseFilingAttachments;
